import React, { useState, useRef } from 'react';
import { useNavigate, useInRouterContext, BrowserRouter } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Fade,
  Tabs,
  Tab,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import ImageIcon from '@mui/icons-material/Image';
import TextIcon from '@mui/icons-material/Description';
import BiotechIcon from '@mui/icons-material/Biotech';
import PlayIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/CheckCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AnimationIcon from '@mui/icons-material/Animation';
import TreeIcon from '@mui/icons-material/AccountTree';
import ChartIcon from '@mui/icons-material/ShowChart';
import InfoIcon from '@mui/icons-material/Info';
import UploadIcon from '@mui/icons-material/CloudUpload';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import { styled } from '@mui/material/styles';

const DemoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const UploadArea = styled(Paper)(({ theme, isDragging }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: theme.spacing(2),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`demo-tabpanel-${index}`}
      aria-labelledby={`demo-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const InteractiveDemoContent = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [demoState, setDemoState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState({
    acoustic: null,
    radiology: null,
    clinical: null,
    pathology: null,
  });
  const [attentionWeights, setAttentionWeights] = useState([0, 0, 0, 0]);
  const [diagnosis, setDiagnosis] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [showVisualization, setShowVisualization] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [logs, setLogs] = useState([]);
  const [fusedEmbedding, setFusedEmbedding] = useState(null);
  const [diseaseDistances, setDiseaseDistances] = useState({
    COPD: 0,
    Pneumonia: 0,
    'Lung Cancer': 0,
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [xaiExplanation, setXaiExplanation] = useState('');

  const fileInputRefs = {
    acoustic: useRef(null),
    radiology: useRef(null),
    clinical: useRef(null),
    pathology: useRef(null),
  };

  const handleFileUpload = (modality, file) => {
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [modality]: file,
      }));
      addLog(` Uploaded ${modality} file: ${file.name}`);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e, modality) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(modality, file);
  };

  const handleFileInput = (e, modality) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(modality, file);
  };

  const addLog = (message) => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message,
    }]);
  };

  const runDemo = async () => {
    setDemoState('processing');
    setProgress(0);
    setActiveStep(0);
    setLogs([]);
    setDiagnosis(null);
    setConfidence(0);
    setAttentionWeights([0, 0, 0, 0]);
    setShowVisualization(false);
    setFusedEmbedding(null);

    addLog(' Starting multimodal fusion demo...');

    await simulateStep(1, 'Extracting modality embeddings...', 20);
    addLog(' Acoustic: Wav2Vec2 → 128D embedding');
    addLog(' Radiology: ResNet50 → 128D embedding');
    addLog(' Clinical: ClinicalBERT → 128D embedding');
    addLog(' Pathology: Prov-GigaPath → 128D embedding');
    setActiveStep(1);

    await simulateStep(2, 'Per-modality projection heads compress to shared semantic space...', 40);
    addLog(' All embeddings aligned to shared disease space');
    setActiveStep(2);

    await simulateStep(3, 'Map each 128D embedding to named clinical concepts...', 60);
    const weights = [0.32, 0.28, 0.20, 0.20];
    setAttentionWeights(weights);
    addLog(` Attention weights: Acoustic=${weights[0].toFixed(2)}, Radiology=${weights[1].toFixed(2)}, Clinical=${weights[2].toFixed(2)}, Pathology=${weights[3].toFixed(2)}`);
    setActiveStep(3);

    await simulateStep(4, 'Align concept-enriched embeddings toward named disease prototypes...', 75);
    addLog(' F(x) + x: Preserving original information');
    setActiveStep(4);

    await simulateStep(5, 'Score each modality\'s contribution to the fusion decision...', 85);
    const diseases = ['COPD', 'Pneumonia', 'Lung Cancer'];
    const distances = { COPD: 0.94, Pneumonia: 0.12, 'Lung Cancer': 0.08 };
    setDiseaseDistances(distances);
    const topDisease = 'COPD';
    setDiagnosis(topDisease);
    setConfidence(distances[topDisease]);

    const explanation = generateXAI(weights, topDisease, distances[topDisease]);
    setXaiExplanation(explanation);
    addLog(` Diagnosis: ${topDisease} (${(distances[topDisease] * 100).toFixed(0)}% confidence)`);

    setActiveStep(9);
    setShowVisualization(true);
    setProgress(100);
    setDemoState('completed');
    addLog(' Fusion complete!');
  };

  const simulateStep = (step, message, targetProgress) => {
    return new Promise((resolve) => {
      addLog(` ${message}`);
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = Math.min(prev + 2, targetProgress);
          if (newProgress >= targetProgress) {
            clearInterval(interval);
            resolve();
            return targetProgress;
          }
          return newProgress;
        });
      }, 150);
    });
  };

  const generateXAI = (weights, diagnosis, confidence) => {
    const modalityNames = ['acoustic', 'radiology', 'clinical', 'pathology'];
    const maxIndex = weights.indexOf(Math.max(...weights));
    const mostImportant = modalityNames[maxIndex];
    let reasoning = `The model diagnosed **${diagnosis}** with ${(confidence * 100).toFixed(0)}% confidence.\n\n`;
    reasoning += `**${mostImportant.charAt(0).toUpperCase() + mostImportant.slice(1)}** was the most influential modality.\n\n`;
    reasoning += 'The model also considered contributions from all four modalities in the shared disease space.';
    return reasoning;
  };

  const renderAttentionBar = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#9B59B6'];
    const labels = ['Acoustic', 'Radiology', 'Clinical', 'Pathology'];
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        {attentionWeights.map((weight, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{labels[index]}</Typography>
              <Typography variant="body2" fontWeight="bold">
                {(weight * 100).toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={weight * 100}
              sx={{
                height: 20,
                borderRadius: 10,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colors[index],
                  borderRadius: 10,
                },
              }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderDiseaseDistances = () => {
    const diseases = Object.keys(diseaseDistances);
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        {diseases.map((disease) => (
          <Box key={disease} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{disease}</Typography>
              <Typography variant="body2" fontWeight="bold">
                {(diseaseDistances[disease] * 100).toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={diseaseDistances[disease] * 100}
              sx={{
                height: 20,
                borderRadius: 10,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: diagnosis === disease ? '#4CAF50' : '#90A4AE',
                  borderRadius: 10,
                },
              }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const resetDemo = () => {
    setDemoState('idle');
    setProgress(0);
    setActiveStep(0);
    setLogs([]);
    setDiagnosis(null);
    setConfidence(0);
    setAttentionWeights([0, 0, 0, 0]);
    setShowVisualization(false);
    setFusedEmbedding(null);
    setXaiExplanation('');
    setUploadedFiles({ acoustic: null, radiology: null, clinical: null, pathology: null });
    addLog(' Demo reset');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <div className="inline-block mb-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
                Interactive Demo
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold leading-tight tracking-tight text-teal">
              Interactive Demo
            </h1>
            <p className="mt-2 text-inkSoft text-base">
              Experience multimodal fusion with CTF Alignment + Attention + Residual connections
            </p>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Animated Demo Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#0F6E56',
                color: '#ffffff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#085041',
                },
              }}
              startIcon={<AnimationIcon />}
              onClick={() => navigate('/animated-demo')}
            >
              Animated Demo
            </Button>

            {/* Case Review Demo Button – same style as other demo buttons */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#187F73',
                color: '#ffffff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#15695F',
                },
              }}
              startIcon={<PsychologyIcon />}
              onClick={() => navigate('/live-diagnostic-case-review')}
            >
              Case Review Demo
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: '#187F73',
                color: '#ffffff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#15695F',
                },
              }}
              startIcon={<RequestPageIcon />}
              onClick={() => navigate('/demo-request')}
            >
              Request Demo
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={3}>
          {/* Upload Section */}
          <Grid item xs={12} md={5}>
            <DemoCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>Upload Modalities</Typography>
                {['acoustic', 'radiology', 'clinical', 'pathology'].map((modality, idx) => {
                  const icons = [MicIcon, ImageIcon, TextIcon, BiotechIcon];
                  const labels = [
                    'Acoustic Data (Audio)',
                    'Radiology Data (X-Ray/CT)',
                    'Clinical Data (Text Notes)',
                    'Pathology Data (Tissue Slide)'
                  ];
                  const Icon = icons[idx];
                  return (
                    <Box key={modality} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        <Icon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {labels[idx]}
                      </Typography>
                      <UploadArea
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, modality)}
                        onClick={() => fileInputRefs[modality].current?.click()}
                      >
                        <input
                          type="file"
                          ref={fileInputRefs[modality]}
                          onChange={(e) => handleFileInput(e, modality)}
                          style={{ display: 'none' }}
                        />
                        {uploadedFiles[modality] ? (
                          <Box>
                            <CheckIcon color="success" sx={{ fontSize: 40 }} />
                            <Typography variant="body2">{uploadedFiles[modality].name}</Typography>
                          </Box>
                        ) : (
                          <Box>
                            <UploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Drag & drop or click to browse
                            </Typography>
                          </Box>
                        )}
                      </UploadArea>
                    </Box>
                  );
                })}
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={runDemo}
                    disabled={demoState === 'processing'}
                    startIcon={<PlayIcon />}
                    sx={{
                      backgroundColor: '#187F73',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#15695F',
                      },
                      '&:disabled': {
                        backgroundColor: '#94a3b8',
                        color: '#cbd5e1',
                      },
                    }}
                  >
                    {demoState === 'processing' ? 'Processing...' : 'Run Fusion Demo'}
                  </Button>
                  <Button variant="outlined" onClick={resetDemo}>Reset</Button>
                </Box>
              </CardContent>
            </DemoCard>
          </Grid>

          {/* Results Section */}
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tab label="Results" />
                  <Tab label="Process" />
                  <Tab label="Logs" />
                  <Tab label="XAI" />
                </Tabs>
                <TabPanel value={selectedTab} index={0}>
                  {diagnosis ? (
                    <Box>
                      <Alert severity="success" sx={{ mb: 3 }}>
                        Diagnosis: <strong>{diagnosis}</strong> — Confidence: {(confidence * 100).toFixed(0)}%
                      </Alert>
                      <Typography variant="h6" gutterBottom>Modality Contribution</Typography>
                      {renderAttentionBar()}
                      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Disease Distances</Typography>
                      {renderDiseaseDistances()}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <PsychologyIcon sx={{ fontSize: 80, color: 'text.secondary' }} />
                      <Typography>Run the demo to see results</Typography>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel value={selectedTab} index={1}>
                  <Stepper activeStep={activeStep} orientation="vertical">
                    <Step>
                      <StepLabel>Step 1: Extract Modality Embeddings</StepLabel>
                      <StepContent>Acoustic, Radiology, Clinical, Pathology</StepContent>
                    </Step>
                    <Step>
                      <StepLabel>Step 2: Project to Shared Space</StepLabel>
                      <StepContent>Four projection heads mapping to 128D</StepContent>
                    </Step>
                    <Step>
                      <StepLabel>Step 3: Ontology Mapping + Attention</StepLabel>
                      <StepContent>Pathology included in attention weights</StepContent>
                    </Step>
                    <Step>
                      <StepLabel>Step 4: Disease Prototype Alignment</StepLabel>
                      <StepContent>CTF Fusion Core</StepContent>
                    </Step>
                  </Stepper>
                </TabPanel>
                <TabPanel value={selectedTab} index={2}>
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {logs.length === 0 ? 'No logs yet.' : logs.map((log, i) => (
                      <Box key={i} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                        <Typography variant="caption">[{log.timestamp}]</Typography>
                        <Typography>{log.message}</Typography>
                      </Box>
                    ))}
                  </Box>
                </TabPanel>
                <TabPanel value={selectedTab} index={3}>
                  {xaiExplanation ? (
                    <Paper sx={{ p: 3 }}>{xaiExplanation}</Paper>
                  ) : (
                    <Typography>Run demo to see explanation</Typography>
                  )}
                </TabPanel>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

const InteractiveDemo = () => {
  const inRouter = useInRouterContext();
  if (inRouter) return <InteractiveDemoContent />;
  return (
    <BrowserRouter>
      <InteractiveDemoContent />
    </BrowserRouter>
  );
};

export default InteractiveDemo;
